/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable } from 'mobx';
import { ExerciseVariation, createExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { BaseTrackingKey } from '../ProgramPortfolio/TrackingKeys';
import { ExerciseId, createExerciseId } from '../ProgramPortfolio/ExerciseId';
import { logger } from '../../Utils/logger';
import type { ExerciseSet } from './ExerciseSet';
import { createExerciseSet } from './ExerciseSet';
import type { WorkoutLog } from './WorkoutLog';
import type { ViewSet } from './ViewSet';

export type ExerciseBlockLog = {
  workoutLog: WorkoutLog;
  exercise: ExerciseVariation;
  exerciseId: ExerciseId;
  sets: ExerciseSet[];
  phaseId?: string;
  exerciseBlockId?: string;
  readonly blockIndex: number;
  readonly setCount: number;
  readonly started: boolean;
  readonly firstSet: ExerciseSet | undefined;
  readonly lastSet: ExerciseSet | undefined;
  readonly prevSet: ExerciseSet | undefined;
  readonly followingSet: ExerciseSet | undefined;
  readonly nextSet: ExerciseSet | undefined;
  readonly lastFinishedSet: ExerciseSet | undefined;
  readonly completedSetsCount: number;
  readonly totalTimeExercising: number;
  readonly isPaused: boolean;
  readonly tons: number;
  readonly prevBlockLog: ExerciseBlockLog | undefined;
  readonly nextBlockLog: ExerciseBlockLog | undefined;
  addSet(setTemplate?: any): ExerciseSet;
  removeSet(viewSet: ViewSet): void;
  removeLastSet(): ExerciseSet | undefined;
  totalOf(setValueKey: BaseTrackingKey): number;
};

export function createExerciseBlockLog(workoutLog: WorkoutLog, json?: Partial<ExerciseBlockLog>): ExerciseBlockLog {
  const exercise = createExerciseVariation((json?.exercise as any) ?? {});
  const result: ExerciseBlockLog = makeAutoObservable({
    workoutLog,
    exercise,
    exerciseId: createExerciseId((json?.exerciseId as any) ?? exercise.exerciseIdentifier),
    sets: [] as ExerciseSet[],
    phaseId: json?.phaseId,
    exerciseBlockId: json?.exerciseBlockId,

    addSet(setTemplate?: any): ExerciseSet {
      if (this.lastSet && !this.lastSet.isStarted) {
        logger('ExerciseBlockLog::addSet() returning lastSet');
        return this.lastSet;
      }
      const set = createExerciseSet(this, { ...setTemplate, workoutLogId: this.workoutLog.id });
      this.sets.push(set);
      return set;
    },

    removeSet(viewSet: ViewSet) {
      const index = this.sets.findIndex((es) => es.setId === viewSet.logSet?.setId);
      if (index !== -1) this.sets.splice(index, 1);
    },

    removeLastSet(): ExerciseSet | undefined {
      const index = this.sets.length - 1;
      if (index !== -1) {
        const set = this.sets[index];
        if (!set.isStarted) {
          const removed = this.sets.splice(index, 1);
          return removed[0];
        }
      }
      return undefined;
    },

    get blockIndex(): number {
      return this.workoutLog.exerciseBlocks.findIndex((b) => b === this);
    },

    get setCount(): number {
      return this.sets.length;
    },

    get started(): boolean {
      return this.firstSet ? this.firstSet.isStarted : false;
    },

    get firstSet(): ExerciseSet | undefined {
      return this.sets.length > 0 ? this.sets[0] : undefined;
    },

    get lastSet(): ExerciseSet | undefined {
      return this.sets.find((s) => !s.isFinished) ?? (this.sets.length > 0 ? this.sets[this.sets.length - 1] : undefined);
    },

    get prevSet(): ExerciseSet | undefined {
      if (!this.lastSet) return this.prevBlockLog?.prevSet;
      return this.lastSet;
    },

    get followingSet(): ExerciseSet | undefined {
      return this.firstSet || this.nextBlockLog?.followingSet;
    },

    get nextSet(): ExerciseSet | undefined {
      return this.firstSet || this.nextBlockLog?.nextSet;
    },

    get lastFinishedSet(): ExerciseSet | undefined {
      for (let i = this.sets.length - 1; i >= 0; i--) {
        if (this.sets[i]?.isFinished) return this.sets[i];
      }
      return undefined;
    },

    get completedSetsCount(): number {
      return this.sets.filter((s) => s.isFinished).length;
    },

    get totalTimeExercising(): number {
      return this.sets.reduce((result: number, set: ExerciseSet) => result + set.duration, 0);
    },

    get isPaused(): boolean {
      return this.sets.some((s) => s.isPaused);
    },

    get tons(): number {
      return this.sets.reduce((result: number, set: ExerciseSet) => result + set.tons, 0);
    },

    get prevBlockLog(): ExerciseBlockLog | undefined {
      if (this.blockIndex > 0) return this.workoutLog.exerciseBlocks[this.blockIndex - 1];
      return undefined;
    },

    get nextBlockLog(): ExerciseBlockLog | undefined {
      const nextIndex = this.blockIndex + 1;
      return nextIndex < this.workoutLog.exerciseBlocks.length ? this.workoutLog.exerciseBlocks[nextIndex] : undefined;
    },

    totalOf(setValueKey: BaseTrackingKey): number {
      return this.sets.reduce(
        (result, set) => result + (set.values.get(setValueKey) || 0) * (set.values.get('REPETITIONS') || 0),
        0,
      );
    },
  });

  // Initialize sets after creating the block
  if (json?.sets) {
    result.sets = (json.sets as any[]).map((s: any) => createExerciseSet(result, s));
  }

  return result;
}
